import { useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Permission } from '../../../../../../../backend/src/auth/permissions/permission';
import { MatterType } from '../../../../../../../backend/src/types/types';
import { useGetSubset } from '../../../../../api/queries/useGetSubset';
import { hasAnyOngoingRunOnAllDocsCase } from '../../../../../helpers/runs';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import useAddDocsToMatter from '@/api/mutations/useAddDocsToMatter';
import { useUpdateRun } from '@/api/mutations/useUpdateRun';
import { useGetFiles } from '@/api/queries/useGetFiles';
import { useGetMatterUploadHistory } from '@/api/queries/useGetMatterUploadHistory';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useHasPermission } from '@/hooks/useHasPermission';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { useMatterBreadcrumbHead } from '@/new/components/breadcrumb-dropdown';
import { CreditBlockTooltip } from '@/new/components/credit-block-tooltip';
import { runNameSchema, RunNameFormData } from '@/new/components/dialogs/run-name-schema';
import { FileUploader } from '@/new/components/file-uploader';
import { Icon } from '@/new/components/icon';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/new/components/shadcn/dialog';
import { FormControl, FormField, FormItem, FormLabel, FormMessage, Form } from '@/new/components/shadcn/form';
import { Input } from '@/new/components/shadcn/input';

export function DocumentUpload() {
  const { matterId } = useParams();
  const { hasPermission } = useHasPermission();
  const canUploadDocuments = hasPermission(Permission.DocumentUpload);
  const navigate = useNavigate();
  const { data: matter } = useGetMatterV2(matterId ?? '');
  const matterBreadcrumbHead = useMatterBreadcrumbHead(matterId ?? '', matter?.name, false);
  const allDocumentsCaseId = matter?.allDocumentsCaseId ?? undefined;
  const { mutateAsync: addDocs, isLoading } = useAddDocsToMatter(matterId ?? '');
  // Single source of truth for upload_history — FileUploader receives this via prop.
  const { data: uploadHistory, isLoading: isUploadHistoryLoading } = useGetMatterUploadHistory(matterId ?? '', {
    refetchInterval: 5_000,
  });
  const { mutateAsync: updateRunName } = useUpdateRun();
  const { data: subset } = useGetSubset(matterId ?? '', allDocumentsCaseId);
  const { data: files } = useGetFiles(matterId ?? '');
  const [filesProcessing, setFilesProcessing] = useState(false);
  const [filesExist, setFilesExist] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const runNameForm = useForm<RunNameFormData>({
    resolver: zodResolver(runNameSchema),
    mode: 'onTouched',
    defaultValues: { runName: '' },
  });

  if (!matterId) return null;
  if (!canUploadDocuments) {
    return <Navigate to={`/matters/${matterId}`} replace />;
  }
  if (matter?.isDraft) {
    return <Navigate to={`/matters/new/${matterId}`} replace />;
  }
  if (matter?.type === MatterType.Sample) {
    return <Navigate to={`/matters/${matterId}`} replace />;
  }
  if (matter && !matter.issues) {
    toast.info('Add Matter issues before processing additional documents.');
    return <Navigate to={`/matters/${matterId}/settings/context`} replace />;
  }

  const isRunOngoing = hasAnyOngoingRunOnAllDocsCase(subset?.runs, allDocumentsCaseId ?? '');
  const hasActiveProcessingUploads = uploadHistory?.some((u) => u.status === 'processing') ?? false;

  const handleProcessDocuments = async () => {
    try {
      if (hasActiveProcessingUploads) {
        toast.info('Please wait for file processing to complete before processing documents.');
        return;
      }

      const result = await addDocs();

      const runName = runNameForm.getValues('runName').trim();
      if (runName) {
        const valid = await runNameForm.trigger('runName');
        if (!valid) return;
      }
      if (result?.runId && runName) {
        await updateRunName({ runId: result.runId, name: runName });
        trackCustomEvent(TrackingEventsV2.RunNameOnUpload, { matterId });
      }

      toast.success('Documents are being processed');
      navigate(`/matters/${matterId}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to add documents');
    }
  };

  return (
    <>
      <Breadcrumb
        items={[
          ...matterBreadcrumbHead,
          { href: `/matters/${matterId}/docs/upload`, label: 'Upload Documents', active: true },
        ]}
      />
      <PageContainer className="border-b">
        <PageHeader>
          <PageTitle text="Upload Documents" />

          <CreditBlockTooltip side="left">
            <Button
              variant="primary"
              disabled={
                filesProcessing ||
                !filesExist ||
                isLoading ||
                isRunOngoing ||
                isUploadHistoryLoading ||
                hasActiveProcessingUploads
              }
              onClick={() => setConfirmOpen(true)}
            >
              {isLoading ? 'Processing...' : 'Process Documents'}
              <Icon className="lucide--arrow-right" />
            </Button>
          </CreditBlockTooltip>
        </PageHeader>
      </PageContainer>

      <Dialog
        open={confirmOpen}
        onOpenChange={(open) => {
          setConfirmOpen(open);
          if (!open) runNameForm.reset();
        }}
      >
        <DialogContent onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Process Documents</DialogTitle>
            <DialogDescription />
          </DialogHeader>

          <div className="mb-6">
            {isLoading ? (
              <div className="flex justify-center p-2 text-gray-500">
                <Icon className="lucide--loader-2 h-6 w-6 animate-spin duration-1500" />
              </div>
            ) : (
              <>
                <p>
                  Are you sure you want to process{' '}
                  <strong>
                    {files?.length ?? 0} {files?.length === 1 ? 'file' : 'files'}
                  </strong>
                  ?
                </p>

                <Form {...runNameForm}>
                  <FormField
                    control={runNameForm.control}
                    name="runName"
                    render={({ field }) => (
                      <FormItem className="mt-6 border-t border-gray-100 pt-4">
                        <div className="mb-2.5">
                          <FormLabel className="mb-0.5 text-sm font-medium text-gray-800">
                            Run name (optional)
                          </FormLabel>
                          <p className="text-xs text-gray-500">
                            Give this run a label so it is easier to identify when filtering or reviewing runs later.
                          </p>
                        </div>
                        <FormControl>
                          <Input {...field} placeholder="e.g. Additional disclosure documents" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </Form>
              </>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button onClick={() => setConfirmOpen(false)} disabled={isLoading}>
              Cancel
            </Button>
            <Button
              data-testid="confirm-button"
              variant="primary"
              onClick={handleProcessDocuments}
              disabled={isLoading}
            >
              Process Documents
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <FileUploader
        uploadHistory={uploadHistory}
        setFilesProcessing={setFilesProcessing}
        setFilesExist={setFilesExist}
      />
    </>
  );
}
